import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, ArrowRight, Trash2, Shield, UserPlus, Pencil, X } from 'lucide-react';
import { userService, rbacService, authService } from '../services/api';

function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', roleId: '' });
  const [editUser, setEditUser] = useState({ username: '', email: '' });

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes] = await Promise.all([
        userService.getAll(),
        rbacService.getRoles(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (err) {
      setError('خطا در دریافت اطلاعات کاربران');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try {
      await userService.delete(id);
      setUsers(users.filter(u => u.id !== id));
    } catch (err) {
      setError('خطا در حذف کاربر');
    }
  };

  const toggleUserRoles = async (user) => {
    if (selectedUser?.id === user.id) {
      setSelectedUser(null);
      return;
    }
    try {
      const res = await rbacService.getUserRoles(user.id);
      setSelectedUser({ ...user, userRoles: res.data });
    } catch (err) {
      setError('خطا در دریافت نقش‌های کاربر');
    }
  };

  const handleAssign = async (userId, roleId) => {
    try {
      await rbacService.assignRole(userId, Number(roleId));
      if (selectedUser?.id === userId) {
        const res = await rbacService.getUserRoles(userId);
        setSelectedUser({ ...selectedUser, userRoles: res.data });
      }
    } catch (err) {
      setError('خطا در اختصاص نقش');
    }
  };

  const handleRemove = async (userId, roleId) => {
    try {
      await rbacService.removeRole(userId, roleId);
      if (selectedUser?.id === userId) {
        const res = await rbacService.getUserRoles(userId);
        setSelectedUser({ ...selectedUser, userRoles: res.data });
      }
    } catch (err) {
      setError('خطا در حذف نقش');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await authService.register({
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
      });

      if (newUser.roleId) {
        await rbacService.assignRole(res.data.id, Number(newUser.roleId));
      }

      setNewUser({ username: '', email: '', password: '', roleId: '' });
      setShowCreate(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ایجاد کاربر');
    }
  };

  const openEdit = (user) => {
    setEditUser({ username: user.username, email: user.email });
    setShowEdit(user.id);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await userService.update(showEdit, editUser);
      setShowEdit(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ویرایش کاربر');
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت کاربران</h1>
              <p className="text-xl text-blue-100">مشاهده و مدیریت کاربران و نقش‌ها</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/admin/articles')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition"
              >
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
              >
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        <div className="mb-6">
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            {showCreate ? <X size={18} /> : <UserPlus size={18} />}
            {showCreate ? 'انصراف' : 'افزودن کاربر جدید'}
          </button>
        </div>

        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">کاربر جدید</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="نام کاربری"
                value={newUser.username}
                onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="email"
                placeholder="ایمیل"
                value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="password"
                placeholder="رمز عبور"
                value={newUser.password}
                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <select
                value={newUser.roleId}
                onChange={e => setNewUser({ ...newUser, roleId: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">بدون نقش</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              ایجاد کاربر
            </button>
          </form>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">در حال بارگذاری...</div>
        ) : (
          <div className="space-y-4">
            {users.map(user => (
              <div key={user.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-6 flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-700">
                      <Users size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{user.username}</h3>
                      <p className="text-gray-600 text-sm">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(user)}
                      className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition text-sm"
                    >
                      <Pencil size={16} />
                      ویرایش
                    </button>
                    <button
                      onClick={() => toggleUserRoles(user)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                    >
                      <Shield size={16} />
                      نقش‌ها
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                    >
                      <Trash2 size={16} />
                      حذف
                    </button>
                  </div>
                </div>

                {showEdit === user.id && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <h4 className="font-bold text-gray-900 mb-4">ویرایش کاربر</h4>
                    <form onSubmit={handleEditSave} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <input
                        type="text"
                        placeholder="نام کاربری"
                        value={editUser.username}
                        onChange={e => setEditUser({ ...editUser, username: e.target.value })}
                        required
                        className="px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="email"
                        placeholder="ایمیل"
                        value={editUser.email}
                        onChange={e => setEditUser({ ...editUser, email: e.target.value })}
                        required
                        className="px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                          ذخیره
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowEdit(null)}
                          className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                        >
                          انصراف
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {selectedUser?.id === user.id && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <h4 className="font-bold text-gray-900 mb-4">نقش‌های کاربر</h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedUser.userRoles?.length ? selectedUser.userRoles.map(r => (
                        <span
                          key={r.id}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {r.name}
                          <button
                            onClick={() => handleRemove(user.id, r.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </span>
                      )) : (
                        <span className="text-gray-500 text-sm">هیچ نقشی اختصاص نیافته است</span>
                      )}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {roles.filter(r => !selectedUser.userRoles?.some(ur => ur.id === r.id)).map(role => (
                        <button
                          key={role.id}
                          onClick={() => handleAssign(user.id, role.id)}
                          className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100 transition"
                        >
                          اختصاص {role.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;
